import { styled } from '@mui/material/styles';
import { Box, Typography, Chip } from '@mui/material';

const honeyColors = {
    primary: '#F1C40F',
    primaryLight: '#F4D03F',
    primaryDark: '#D4AC0D',
    amber: '#FFB74D',
    cream: '#FFF8E1',
    honeycomb: '#D4A574',
    textPrimary: '#3E2723',
    textSecondary: '#5D4037',
    textMuted: '#8D6E63',
    gradientPrimary: 'linear-gradient(135deg, #F1C40F 0%, #FFB74D 100%)',
    shadowLight: '0 2px 8px rgba(212, 165, 116, 0.15)',
    shadowMedium: '0 4px 16px rgba(212, 165, 116, 0.25)',
    border: '#F5E6B3',
    surface: '#FFFFFF',
};

export const AboutContainer = styled(Box)(({ theme }) => ({
    background: honeyColors.surface,
    borderRadius: '20px',
    padding: '28px',
    marginBottom: '24px',
    border: `1px solid ${honeyColors.border}`,
    boxShadow: honeyColors.shadowLight,
    position: 'relative',

    [theme.breakpoints.down('sm')]: {
        padding: '20px',
        borderRadius: '16px',
    },
}));

export const AboutHeader = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',

    '& .header-icon': {
        width: '44px',
        height: '44px',
        borderRadius: '12px',
        background: `${honeyColors.primary}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        '& svg': {
            fontSize: '22px',
            color: honeyColors.primaryDark,
        },
    },
});

export const AboutTitle = styled(Typography)({
    fontWeight: 700,
    fontSize: '1.25rem',
    color: honeyColors.textPrimary,
});

export const AboutDescription = styled(Typography)(({ theme, expanded }) => ({
    color: honeyColors.textSecondary,
    fontSize: '0.95rem',
    lineHeight: 1.7,
    marginBottom: '20px',
    
    // Truncate if not expanded
    ...(!expanded && {
        display: '-webkit-box',
        WebkitLineClamp: 4,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    }),

    [theme.breakpoints.down('sm')]: {
        fontSize: '0.9rem',
    },
}));

export const ReadMoreButton = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: honeyColors.primary,
    fontWeight: 600,
    fontSize: '0.85rem',
    cursor: 'pointer',
    marginBottom: '20px',
    transition: 'all 0.2s ease',

    '&:hover': {
        color: honeyColors.primaryDark,
        transform: 'translateX(2px)',
    },

    '& svg': {
        fontSize: '16px',
        transition: 'transform 0.2s ease',
    },
});

export const StatsGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginBottom: '20px',

    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },

    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '10px',
    },
}));

export const StatCard = styled(Box)({
    background: honeyColors.cream,
    borderRadius: '14px',
    padding: '16px 12px',
    textAlign: 'center',
    border: `1px solid ${honeyColors.border}`,
    transition: 'all 0.2s ease',

    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: honeyColors.shadowLight,
        borderColor: honeyColors.primary,
    },

    '& .stat-value': {
        fontWeight: 800,
        fontSize: '1.5rem',
        color: honeyColors.textPrimary,
        lineHeight: 1.2,
        background: honeyColors.gradientPrimary,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
    },

    '& .stat-label': {
        fontSize: '0.75rem',
        color: honeyColors.textMuted,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginTop: '4px',
    },
});

export const TagsContainer = styled(Box)({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
});

export const CategoryTag = styled(Chip)({
    backgroundColor: `${honeyColors.primary}15`,
    color: honeyColors.textPrimary,
    fontWeight: 600,
    fontSize: '0.8rem',
    border: `1px solid ${honeyColors.primary}30`,
    borderRadius: '20px',
    
    '&:hover': {
        backgroundColor: `${honeyColors.primary}25`,
    },

    '& .MuiChip-icon': {
        color: honeyColors.primaryDark,
    },
});

export const SocialLinks = styled(Box)({
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: `1px solid ${honeyColors.border}`,

    '& .social-link': {
        width: '36px',
        height: '36px',
        borderRadius: '10px',
        background: honeyColors.cream,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: `1px solid ${honeyColors.border}`,

        '& svg': {
            fontSize: '18px',
            color: honeyColors.textSecondary,
        },

        '&:hover': {
            background: honeyColors.gradientPrimary,
            borderColor: honeyColors.primary,
            transform: 'translateY(-2px)',

            '& svg': {
                color: honeyColors.textPrimary,
            },
        },
    },
});

export const CollapsedAbout = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    background: honeyColors.surface,
    borderRadius: '14px',
    marginBottom: '16px',
    border: `1px solid ${honeyColors.border}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',

    '&:hover': {
        borderColor: honeyColors.primary,
        boxShadow: honeyColors.shadowLight,
    },

    '& .collapsed-left': {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },

    '& .collapsed-title': {
        fontWeight: 600,
        color: honeyColors.textPrimary,
        fontSize: '0.95rem',
    },

    '& .collapsed-preview': {
        color: honeyColors.textMuted,
        fontSize: '0.85rem',
        maxWidth: '400px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
});
